import { storeToRefs } from 'pinia';
import { watch } from 'vue';
import { useSentry } from '~/composables/useSentry';
import { useAuthStore } from '~/stores/auth';
import { useUsersStore } from '~/stores/users';

export default defineNuxtPlugin(() => {
  const usersStore = useUsersStore();
  const authStore = useAuthStore();
  const { authUserData, authUserMeta } = storeToRefs(usersStore);
  const { isAuthenticated } = storeToRefs(authStore);
  const { setUser, setContext, setTag } = useSentry();

  watch(
    authUserData,
    (user) => {
      if (!user) {
        setUser(null);
        setContext('profile', null);
        setTag('position_slug', 'anonymous');
        setTag('primary_role', 'anonymous');
        return;
      }

      const roles = Array.isArray(user.roles) ? user.roles.map(role => String(role)) : [];
      const positionSlug = user.position?.slug ?? null;

      const sentryUser: Record<string, unknown> = {
        id: String(user.id),
        email: user.email,
        username: user.username,
        segment: roles[0],
        data: {
          roles,
          positionSlug,
        },
      };

      setUser(sentryUser);

      setContext('profile', {
        position: user.position?.slug,
        roles: user.roles,
      });

      if (user.position?.slug) {
        setTag('position_slug', user.position.slug);
      }

      if (user.roles?.length) {
        setTag('primary_role', user.roles[0]);
      }
    },
    { immediate: true },
  );

  watch(
    authUserMeta,
    (meta) => {
      if (!meta) {
        setContext('permissions', null);
        return;
      }

      setContext('permissions', {
        count: meta.permissions.length,
        values: meta.permissions,
      });
    },
    { immediate: true },
  );

  watch(
    isAuthenticated,
    (isAuthenticated) => {
      if (isAuthenticated) {
        return;
      }

      setUser(null);
      setContext('profile', null);
      setContext('permissions', null);
    },
    { immediate: true },
  );
});
